/** Summary shown in the insurance info hover tooltip. */
export interface PbmTooltipSummary {
  count: number;
  names: string[];
}

interface ApiDetailsResponse {
  details?: Record<string, unknown>[];
}

/**
 * Adds a trimmed insurance/PBM name to the summary when it is non-empty and unique.
 *
 * @param names - Display names collected so far
 * @param seenKeys - Lowercased keys used for deduplication
 * @param rawValue - Raw field value from an API record
 */
const addDistinctInsuranceName = (
  names: string[],
  seenKeys: Set<string>,
  rawValue: unknown,
): void => {
  if (rawValue == null) {
    return;
  }

  const displayName = String(rawValue).trim();
  if (displayName === "") {
    return;
  }

  const dedupeKey = displayName.toLowerCase();
  if (seenKeys.has(dedupeKey)) {
    return;
  }

  seenKeys.add(dedupeKey);
  names.push(displayName);
};

/**
 * Builds PBM count and distinct insurance names from insurance-details and eligibility APIs.
 * Insurance-details records are included only when `type` is `PBM`.
 *
 * @param insuranceResponse - Response from get-insurance-details
 * @param eligibilityResponse - Response from get-patient-eligibility-details
 * @returns PBM count with distinct insurer/PBM names for tooltip display
 */
export const getPbmTooltipSummary = (
  insuranceResponse: ApiDetailsResponse | null | undefined,
  eligibilityResponse: ApiDetailsResponse | null | undefined,
): PbmTooltipSummary => {
  const insuranceDetails = insuranceResponse?.details;
  const eligibilityDetails = eligibilityResponse?.details;
  const insuranceRecords = Array.isArray(insuranceDetails)
    ? insuranceDetails
    : [];
  const eligibilityRecords = Array.isArray(eligibilityDetails)
    ? eligibilityDetails
    : [];

  const names: string[] = [];
  const seenKeys = new Set<string>();

  for (const record of insuranceRecords) {
    const recordType = String(record.type ?? "")
      .trim()
      .toLowerCase();
    if (recordType !== "pbm") {
      continue;
    }

    addDistinctInsuranceName(names, seenKeys, record.insurer);
  }

  for (const record of eligibilityRecords) {
    addDistinctInsuranceName(names, seenKeys, record.insurance);
  }

  return {
    count: names.length,
    names,
  };
};
